/**
 * Xác thực danh tính (eKYC) — học viên (TT 83 C1-c; TT 56 Đ5.4).
 *
 * Luồng: đọc & đồng ý consent riêng (ảnh khuôn mặt = DLCN nhạy cảm) → chụp CCCD 2 mặt +
 * selfie → gửi → kết quả đạt / chờ duyệt / không đạt. Có nút rút đồng ý (xoá toàn bộ ảnh).
 * Ẩn hoàn toàn khi `feature_flags.identity_ekyc` tắt.
 */
import { useState } from "react";
import { Alert, Button, Card, Checkbox, Popconfirm, Result, Space, Spin, Tag, Typography, message } from "antd";
import { SafetyCertificateOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  type EkycStatus,
  errorDetail,
  getIdentityStatus,
  grantIdentityConsent,
  revokeIdentityConsent,
  submitEkyc,
} from "@/features/identity/api";
import { IDENTITY_CONSENT_POINTS } from "@/features/identity/consentText";
import { ImageCapture } from "@/features/identity/ImageCapture";
import { useAuthStore } from "@/stores/auth";
import { formatDateTime } from "@/lib/formatDate";

const { Title, Paragraph, Text } = Typography;

const STATUS_TAG: Record<EkycStatus, { color: string; label: string }> = {
  none: { color: "default", label: "Chưa xác thực" },
  passed: { color: "green", label: "Đã xác thực" },
  needs_review: { color: "gold", label: "Đang chờ duyệt" },
  failed: { color: "red", label: "Không đạt — cần làm lại" },
};

export function IdentityVerify() {
  const enabled = useAuthStore((s) => s.user?.feature_flags?.identity_ekyc);
  const queryClient = useQueryClient();
  const [agreed, setAgreed] = useState(false);
  const [front, setFront] = useState<File | null>(null);
  const [back, setBack] = useState<File | null>(null);
  const [selfie, setSelfie] = useState<File | null>(null);

  const statusQuery = useQuery({
    queryKey: ["identity-status"],
    queryFn: getIdentityStatus,
    enabled: !!enabled,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["identity-status"] });

  const consentMut = useMutation({
    mutationFn: () => grantIdentityConsent(statusQuery.data!.consent.policy_version),
    onSuccess: refresh,
    onError: (err) => message.error(errorDetail(err, "Không ghi nhận được đồng ý")),
  });

  const submitMut = useMutation({
    mutationFn: () => submitEkyc({ id_front: front!, id_back: back!, selfie: selfie! }),
    onSuccess: (res) => {
      if (res.status === "passed") message.success("Xác thực thành công");
      else if (res.status === "needs_review") message.info("Đã gửi — đang chờ trường duyệt");
      else message.warning(res.reason || "Không đạt — vui lòng chụp lại");
      setFront(null);
      setBack(null);
      setSelfie(null);
      refresh();
    },
    onError: (err) => message.error(errorDetail(err, "Gửi xác thực thất bại")),
  });

  const revokeMut = useMutation({
    mutationFn: revokeIdentityConsent,
    onSuccess: (res) => {
      message.success(`Đã rút đồng ý và xoá ${res.deleted_images} ảnh`);
      setAgreed(false);
      refresh();
    },
    onError: () => message.error("Không rút được đồng ý"),
  });

  if (!enabled) {
    return <Result status="info" title="Tính năng xác thực danh tính chưa được bật" />;
  }
  if (statusQuery.isPending) {
    return <Spin style={{ display: "block", margin: "64px auto" }} />;
  }
  if (statusQuery.isError || !statusQuery.data) {
    return <Result status="error" title="Không tải được trạng thái xác thực" />;
  }

  const { consent, ekyc, required_for_exam } = statusQuery.data;
  const tag = STATUS_TAG[ekyc.status];
  const canSubmit = !!front && !!back && !!selfie && !submitMut.isPending;

  return (
    <div style={{ maxWidth: 760, margin: "0 auto" }} data-testid="identity-verify">
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              <SafetyCertificateOutlined /> Xác thực danh tính
            </Title>
            <Space style={{ marginTop: 8 }}>
              <Tag color={tag.color} data-testid="ekyc-status">
                {tag.label}
              </Tag>
              {ekyc.verified_at && <Text type="secondary">lúc {formatDateTime(ekyc.verified_at)}</Text>}
            </Space>
          </div>

          {required_for_exam && ekyc.status !== "passed" && (
            <Alert
              type="warning"
              showIcon
              message="Trường yêu cầu xác thực danh tính trước khi vào các bài thi có giám sát."
            />
          )}

          {!consent.granted ? (
            <Alert
              type="info"
              showIcon
              message="Đồng ý xử lý ảnh khuôn mặt và giấy tờ tuỳ thân"
              description={
                <Space direction="vertical">
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {IDENTITY_CONSENT_POINTS.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                  <Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)}>
                    Tôi đã đọc và đồng ý (phiên bản {consent.policy_version})
                  </Checkbox>
                  <Button
                    type="primary"
                    disabled={!agreed}
                    loading={consentMut.isPending}
                    onClick={() => consentMut.mutate()}
                  >
                    Đồng ý và tiếp tục
                  </Button>
                </Space>
              }
            />
          ) : (
            <>
              {ekyc.status !== "passed" && ekyc.status !== "needs_review" && (
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                  <Paragraph style={{ margin: 0 }}>
                    Chụp rõ nét, đủ sáng, không che khuất. Ảnh khuôn mặt nhìn thẳng, không đeo kính
                    râm/khẩu trang.
                  </Paragraph>
                  <ImageCapture
                    label="Căn cước công dân — mặt trước"
                    facing="environment"
                    value={front}
                    onChange={setFront}
                    testId="input-id-front"
                  />
                  <ImageCapture
                    label="Căn cước công dân — mặt sau"
                    facing="environment"
                    value={back}
                    onChange={setBack}
                    testId="input-id-back"
                  />
                  <ImageCapture
                    label="Ảnh khuôn mặt"
                    hint="Dùng camera trước, nhìn thẳng vào màn hình."
                    facing="user"
                    value={selfie}
                    onChange={setSelfie}
                    testId="input-selfie"
                  />
                  <Button
                    type="primary"
                    disabled={!canSubmit}
                    loading={submitMut.isPending}
                    onClick={() => submitMut.mutate()}
                  >
                    Gửi xác thực
                  </Button>
                </Space>
              )}
              {ekyc.status === "needs_review" && (
                <Alert
                  type="info"
                  showIcon
                  message="Hồ sơ đang chờ quản trị viên của trường duyệt. Bạn sẽ thấy kết quả tại đây."
                />
              )}
              <Popconfirm
                title="Rút đồng ý xác thực?"
                description="Toàn bộ ảnh sẽ bị xoá ngay. Bạn không vào được bài thi bắt buộc xác thực cho tới khi xác thực lại."
                okText="Rút đồng ý"
                okButtonProps={{ danger: true }}
                onConfirm={() => revokeMut.mutate()}
              >
                <Button danger type="link" loading={revokeMut.isPending} style={{ padding: 0 }}>
                  Rút đồng ý và xoá ảnh của tôi
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      </Card>
    </div>
  );
}
